import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Mk2Response,
    Pagination
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import { CompanyFilterInput, SupplierDetailResponse } from '../../model/models';
import { GetSupplierBasicInformationRequest } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/supplier-details/get-supplier-basic-information';
import { GetSupplierProgramInformationResponse } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/supplier-details/get-supplier-program-information';

@Injectable({ providedIn: 'root' })
export class GetSupplierProgramInformationService extends ApiService<
    GetSupplierBasicInformationRequest,
    GetSupplierProgramInformationResponse
> {
    public requestType = GetSupplierBasicInformationRequest;
    public responseType = GetSupplierProgramInformationResponse;
    public endpointName = 'BuyerAccount/SupplierDetails/GetSupplierProgramInformation';
    public serviceName = 'financing';

    private _isPayLaterEnabled: boolean;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName =
                        'BuyerAccount/SupplierDetails/GetSupplierProgramInformation';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: GetSupplierBasicInformationRequest
    ): { query: string } | GetSupplierBasicInformationRequest {
        if (this._isPayLaterEnabled) {
            const query = this._mapToGraphQLQuery(req);
            return { query: `{${jsonToGraphQLQuery(query)}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<SupplierDetailResponse> | GetSupplierProgramInformationResponse
    ): GetSupplierProgramInformationResponse {
        if (this._isPayLaterEnabled) {
            const response = (res as GraphQLResponse<SupplierDetailResponse>).data.supplierDetail;

            return new GetSupplierProgramInformationResponse({
                earlyPaidAvgDays: response.supplierDetail.avgDaysPaidEarly.toString(),
                enrollmentDate: response.supplierDetail.enrollmentDate,
                percentUsed: response.supplierDetail.usedPercentage.toString(),
                status: response.supplierDetail.status,
                totalOpenInvoices: response.supplierDetail.openInvoices,
                buyerPaidEarlyPaymentFeePercentage:
                    response.supplierDetail.buyerPaidEarlyPaymentFeePercentage,
                buyerPaidEarlyPaymentFeePercentageVisible:
                    response.supplierDetail.isBuyerPaidEarlyPaymentFeePercentageVisible
            });
        }
        return res as GetSupplierProgramInformationResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    private _mapToGraphQLQuery(req: GetSupplierBasicInformationRequest): any {
        const input: CompanyFilterInput = {
            supplierIds: [req.supplierKey]
        };

        return {
            supplierDetail: {
                __args: { input },
                supplierDetail: {
                    openInvoices: true,
                    usedPercentage: true,
                    avgDaysPaidEarly: true,
                    relationshipKey: true,
                    status: true,
                    enrollmentDate: true,
                    defaultBankAccountKey: true,
                    buyerPaidEarlyPaymentFeePercentage: true,
                    isBuyerPaidEarlyPaymentFeePercentageVisible: true
                }
            }
        };
    }
}
